import di from 'di';
import {cloneDeep, difference} from 'lodash'

import AllAssetFolders from 'common/gql/query/store/assets/AllAssetFolders.gql';
import AllAssets from 'common/gql/query/store/assets/AllAssets.gql';
import AllAssetsWithTags from 'common/gql/query/store/assets/AllAssetsWithTags.gql';
import AllAssetsSlim from 'common/gql/query/store/assets/AllAssetsSlim.gql';
import AllPendingAssets from 'common/gql/query/store/assets/AllPendingAssets.gql';

export const AssetStoreAction = {
    POPULATE_ASSETS: 'POPULATE_ASSETS',
    POPULATE_PENDING_ASSETS: 'POPULATE_PENDING_ASSETS',
    LOAD_ASSETS: 'LOAD_ASSETS',
    LOAD_PENDING_ASSETS: 'LOAD_PENDING_ASSETS',
    LOAD_ASSET_FOLDERS: 'LOAD_ASSET_FOLDERS',
    EXECUTE_ASSETS_OPERATION: 'EXECUTE_ASSETS_OPERATION'
};

export const AssetStoreMutation = {
    SET_ASSETS: 'SET_ASSETS',
    SET_PENDING_ASSETS: 'SET_PENDING_ASSETS',
    SET_ASSET_FOLDERS: 'SET_ASSET_FOLDERS',
    ADD_ASSET: 'ADD_ASSET',
    ADD_PENDING_ASSET: 'ADD_PENDING_ASSET',
    REMOVE_ASSET: 'REMOVE_ASSET',
    REMOVE_PENDING_ASSET: 'REMOVE_PENDING_ASSET',
    ADD_ASSET_FOLDER: 'ADD_ASSET_FOLDER',
    REMOVE_ASSET_FOLDER: 'REMOVE_ASSET_FOLDER',
    UPDATE_ASSET_FOLDER: 'UPDATE_ASSET_FOLDER',
    SET_LOGO_URL: 'SET_LOGO_URL'
};

function createEmptyState() {

    return {

        assets: null,
        pendingAssets: null,
        assetFolders: null,
        logoUrl: null,

        assetsContainTags: null
    };
}

export const assetsStore = {

    state() {

        return createEmptyState();
    },

    getters: {

        getAsset: state => id => {
            return state.assets.find(asset => asset.id == id);
        },

        getStockLibraryAssets: state => {

            const pendingAssets = state.pendingAssets?.filter(asset => asset.organizationId == null);
            const assets = state.assets?.filter(asset => asset.organizationId == null).sort((a, b) => b.sortIndex - a.sortIndex);

            return [...pendingAssets, ...assets];
        },

        getCustomLibraryAssets: state => {

            const pendingAssets = state.pendingAssets?.filter(asset => asset.organizationId != null) ?? [];
            const assets = state.assets?.filter(asset => asset.organizationId != null).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) ?? [];

            return [...pendingAssets, ...assets];
        }
    },

    mutations: {

        clear(state) {
            Object.assign(state, createEmptyState());
        },

        setAssetsContainTags(state, value) {

            state.assetsContainTags = value;
        },

        [AssetStoreMutation.SET_ASSETS](state, assets) {

            state.assets = assets;
        },

        [AssetStoreMutation.SET_PENDING_ASSETS](state, assets) {

            state.pendingAssets = assets;
        },

        [AssetStoreMutation.SET_ASSET_FOLDERS](state, assetFolders) {

            state.assetFolders = assetFolders;
        },

        [AssetStoreMutation.ADD_ASSET](state, asset) {

            if (state.assets == null) {
                state.assets = [asset];
                return;
            }

            state.assets = [...state.assets, asset];
        },

        [AssetStoreMutation.ADD_PENDING_ASSET](state, asset) {

            if (state.pendingAssets == null) {
                state.pendingAssets = [asset];
                return;
            }

            state.pendingAssets = [...state.pendingAssets, asset];
        },

        [AssetStoreMutation.REMOVE_ASSET](state, asset) {

            if (state.assets == null) {
                return;
            }

            state.assets = state.assets.filter(a => a.id !== asset.id);
        },

        [AssetStoreMutation.REMOVE_PENDING_ASSET](state, asset) {

            if (state.pendingAssets == null) {
                return;
            }

            state.pendingAssets = state.pendingAssets.filter(a => a.id !== asset.id);
        },

        [AssetStoreMutation.ADD_ASSET_FOLDER](state, folder) {

            if (state.assetFolders == null) {
                state.assetFolders = [folder];
                return;
            }

            state.assetFolders = [...state.assetFolders, folder];
        },

        [AssetStoreMutation.REMOVE_ASSET_FOLDER](state, folder) {

            if (state.assetFolders == null) {
                return;
            }

            state.assetFolders = state.assetFolders.filter(a => a.id !== folder.id);
        },

        [AssetStoreMutation.UPDATE_ASSET_FOLDER](state, id, data) {

            const folder = state.assetFolders?.find(folder => folder.id === id);
            if (!folder) {
                return;
            }

            const index = state.assetFolders.indexOf(folder);
            state.assetFolders[index] = {
                ...folder,
                ...data
            }
        },

        [AssetStoreMutation.SET_LOGO_URL](state, logoUrl) {

            state.logoUrl = logoUrl;
        },
    },

    actions: {

        async [AssetStoreAction.POPULATE_ASSETS](ctx, options) {

            if (!ctx.state.assets || (options?.tags && !ctx.state.assetsContainTags)) {
                await ctx.dispatch(AssetStoreAction.LOAD_ASSETS, options);
            }

            if (!ctx.state.assetFolders) {
                await ctx.dispatch(AssetStoreAction.LOAD_ASSET_FOLDERS);
            }
        },

        async [AssetStoreAction.POPULATE_PENDING_ASSETS](ctx, options) {

            if (!ctx.state.pendingAssets) {

                await ctx.dispatch(AssetStoreAction.LOAD_PENDING_ASSETS, options);

                // Auto refresh pending assets after population
                setInterval(() => ctx.dispatch(AssetStoreAction.LOAD_PENDING_ASSETS, {force: true}), 30 * 1000);
            }
        },

        async [AssetStoreAction.LOAD_ASSETS](ctx, options) {

            const apollo = di.get('apollo');

            let query = AllAssets;

            if (options?.slim) {
                query = AllAssetsSlim;
            } else if (options?.tags) {
                query = AllAssetsWithTags;
            }

            const results = await apollo.query({
                query: query,
                fetchPolicy: options?.force ? 'network-only' : null
            });

            const assets = results.data?.allAssets?.items;

            ctx.commit(AssetStoreMutation.SET_ASSETS, cloneDeep(assets));
            ctx.commit('setAssetsContainTags', options?.tags || false);
        },

        async [AssetStoreAction.LOAD_PENDING_ASSETS](ctx, options) {

            const apollo = di.get('apollo');

            const results = await apollo.query({
                query: AllPendingAssets,
                fetchPolicy: options?.force ? 'network-only' : null
            });

            const oldPendingAssetIds = ctx.state.pendingAssets?.map(asset => asset.id) || [];
            const assets = results.data?.allPendingAssets;
            const newAssetIds = assets.map(asset => asset.id)

            // On pending assets change, reload assets as well
            if (difference(oldPendingAssetIds, newAssetIds).length) {

                await ctx.dispatch(AssetStoreAction.LOAD_ASSETS, {
                    force: true,
                    tags: ctx.assetsContainTags
                });
            }

            ctx.commit(AssetStoreMutation.SET_PENDING_ASSETS, cloneDeep(assets));
        },

        async [AssetStoreAction.LOAD_ASSET_FOLDERS](ctx, options) {

            const apollo = di.get('apollo');

            const results = await apollo.query({
                query: AllAssetFolders,
                fetchPolicy: options?.force ? 'network-only' : null
            });

            const assetFolders = results.data?.allAssetFolders?.items;
            ctx.commit(AssetStoreMutation.SET_ASSET_FOLDERS, cloneDeep(assetFolders));
        },

        async [AssetStoreAction.EXECUTE_ASSETS_OPERATION](ctx) {

            return ctx.dispatch(AssetStoreAction.LOAD_ASSETS, {force: true});
        }
    }
};
